<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-card" role="dialog" aria-modal="true">
      <header class="modal-header">
        <h2>Override price — {{ tenantName || '—' }}</h2>
        <button type="button" class="close-x" @click="close" aria-label="Close">×</button>
      </header>
      <div class="modal-body">
        <p class="muted small">
          Sets the pricing mode on this tenant. The calculated price is
          <strong>${{ formatNumber(monthlyPriceCalculated) }}</strong> / month.
        </p>

        <fieldset class="modes">
          <label class="mode">
            <input type="radio" v-model="mode" value="calculated" />
            <div>
              <div class="mode-title">Calculated</div>
              <div class="mode-detail">${{ formatNumber(monthlyPriceCalculated) }} / month — derived from population.</div>
            </div>
          </label>
          <label class="mode">
            <input type="radio" v-model="mode" value="free" />
            <div>
              <div class="mode-title">Free</div>
              <div class="mode-detail">$0 / month. Polygon will show the tenant name on the public map.</div>
            </div>
          </label>
          <label class="mode">
            <input type="radio" v-model="mode" value="custom" />
            <div>
              <div class="mode-title">Custom</div>
              <div class="mode-detail">
                Manual amount.
                <span v-if="mode === 'custom'" class="custom-input-wrap">
                  <span class="custom-prefix">$</span>
                  <input
                      type="number"
                      v-model.number="customAmount"
                      class="custom-input"
                      min="0"
                  />
                  <span class="custom-suffix">/ month</span>
                </span>
              </div>
            </div>
          </label>
        </fieldset>

        <label class="field">
          <span>Reason (optional)</span>
          <textarea v-model="reason" rows="2"></textarea>
        </label>

        <div v-if="error" class="alert">{{ error }}</div>
      </div>
      <footer class="modal-footer">
        <button type="button" class="btn ghost" @click="close">Cancel</button>
        <button
            type="button"
            class="btn primary"
            :disabled="!valid || saving"
            @click="save"
        >{{ saving ? 'Saving…' : 'Save' }}</button>
      </footer>
    </div>
  </div>
</template>

<script>
import { setMarketPrice, humanizeAxiosError } from './markets_api';

export default {
  name: 'OverridePriceModal',
  props: {
    show: { type: Boolean, required: true },
    tenantId: { type: String, required: true },
    tenantName: { type: String, default: '' },
    pricingMode: { type: String, default: 'calculated' },
    monthlyPriceCalculated: { type: Number, default: 0 },
    monthlyPriceOverride: { type: Number, default: null },
  },
  data() {
    return {
      mode: this.pricingMode || 'calculated',
      customAmount: typeof this.monthlyPriceOverride === 'number' && this.pricingMode === 'custom'
        ? this.monthlyPriceOverride : this.monthlyPriceCalculated || 0,
      reason: '',
      saving: false,
      error: '',
    };
  },
  computed: {
    valid() {
      if (this.mode === 'custom') {
        return typeof this.customAmount === 'number' && this.customAmount >= 0;
      }
      return ['calculated', 'free'].includes(this.mode);
    },
  },
  methods: {
    formatNumber(n) {
      if (typeof n !== 'number') return '—';
      return n.toLocaleString('en-US');
    },
    close() { this.$emit('update:show', false); },
    async save() {
      this.error = '';
      this.saving = true;
      try {
        const payload = { pricing_mode: this.mode, reason: this.reason || null };
        if (this.mode === 'custom') payload.override = Number(this.customAmount);
        await setMarketPrice(this.tenantId, payload);
        this.$emit('saved');
      } catch (e) {
        this.error = humanizeAxiosError(e);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed; inset: 0; z-index: 10010;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
}
.modal-card {
  background: #fff; border-radius: 12px;
  width: 520px; max-width: 100%;
  max-height: calc(100vh - 64px); overflow: hidden;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 60px rgba(0,0,0,0.18);
}
.modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 20px; border-bottom: 1px solid #eef1f5;
}
.modal-header h2 { margin: 0; font-size: 16px; font-weight: 700; color: #061D47; }
.close-x {
  background: transparent; border: 0; cursor: pointer; font-size: 24px;
  color: #6b7280; line-height: 1; padding: 0 4px;
}
.close-x:hover { color: #1f2937; }
.modal-body {
  padding: 16px 20px; overflow-y: auto; flex: 1;
}
.modal-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 20px; border-top: 1px solid #eef1f5; background: #fafbfd;
}
.muted { color: #6b7280; }
.small { font-size: 13px; }
.modes { border: 0; padding: 0; margin: 12px 0 16px; display: flex; flex-direction: column; gap: 10px; }
.mode {
  display: flex; gap: 10px; align-items: flex-start;
  padding: 10px 12px; border: 1px solid #eef1f5; border-radius: 8px;
  cursor: pointer;
}
.mode:hover { background: #fafbfd; }
.mode input[type="radio"] { margin-top: 4px; }
.mode-title { font-weight: 600; color: #1f2937; font-size: 14px; }
.mode-detail { color: #6b7280; font-size: 12px; margin-top: 2px; }
.custom-input-wrap {
  display: inline-flex; align-items: center; gap: 4px;
  margin-left: 8px;
}
.custom-prefix, .custom-suffix { color: #6b7280; font-size: 12px; }
.custom-input {
  width: 100px; padding: 4px 8px;
  border: 1px solid #c2cbe0; border-radius: 4px;
  font-size: 13px;
}
.field { display: flex; flex-direction: column; gap: 4px; }
.field span { font-size: 12px; color: #6b7280; }
.field textarea {
  width: 100%; box-sizing: border-box;
  padding: 8px 12px; border: 1px solid #c2cbe0; border-radius: 6px;
  font-size: 13px; font-family: inherit; resize: vertical; min-height: 60px;
}
.alert {
  margin-top: 12px;
  padding: 8px 12px; background: #fff5f5;
  border: 1px solid #fbd5d5; border-radius: 6px;
  color: #7E0F0F; font-size: 13px;
}
.btn {
  border: 0; border-radius: 6px;
  padding: 8px 16px; font-size: 14px; font-weight: 600;
  cursor: pointer;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn.ghost { background: transparent; color: #4b5563; }
.btn.ghost:hover:not(:disabled) { background: #eef1f5; }
.btn.primary { background: #061D47; color: #fff; }
.btn.primary:hover:not(:disabled) { background: #0b2a66; }
</style>
